import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import { toast } from "components/ui/toast/Toast";
import { betterAuthErrorText } from "lib/auth/authClient";
import { useDisableTwoFactor } from "lib/hooks/api/auth/useDisableTwoFactor";
import { useGenerateBackupCodes } from "lib/hooks/api/auth/useGenerateBackupCodes";
import Label from "../../ui/input/Label";
import { ErrorText } from "./utils";
import { BackupCodesList } from "./VerifyMFA";

type FormValues = { password: string };

export default function TurnOffMFA({
  companyRequires2FA,
  onSuccess,
}: {
  companyRequires2FA: boolean;
  onSuccess: () => Promise<void>;
}) {
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { password: "" },
  });

  // Both actions re-authenticate with the password, so disable them until one is entered.
  const password = useWatch({ control, name: "password" });

  // Fresh codes from a regenerate, shown once.
  const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);

  // Both actions re-authenticate with the password, so a server error (e.g. wrong password) is
  // surfaced as an error on the password field (rendered below the input).
  const onPasswordError = (error: unknown) =>
    setError("password", { type: "server", message: betterAuthErrorText(error) });

  const { mutate: disable, isLoading: isDisableLoading } = useDisableTwoFactor({
    onSuccess: async () => {
      toast.success("Two-factor authentication disabled");
      // Refresh the session (bypassing the cookie cache) so the page returns to the enrol view.
      await onSuccess();
    },
    onError: onPasswordError,
  });

  const onDisable = handleSubmit((values) => disable({ password: values.password }));

  const { mutate: regenerateBackupCodes, isLoading: isRegenerateLoading } = useGenerateBackupCodes({
    onSuccess: (data) => {
      setNewBackupCodes(data.backupCodes);
      toast.success("Backup codes regenerated");
    },
    onError: onPasswordError,
  });

  const onRegenerate = handleSubmit((values) =>
    regenerateBackupCodes({ password: values.password }),
  );

  return (
    <Flex gap={16} flexDirection="column">
      <Text>
        Two-factor authentication is <strong>on</strong> for your account.
      </Text>

      {companyRequires2FA && (
        <Text color="sysTextSecondary">
          Your organisation requires two-factor authentication, so it can&apos;t be turned off.
        </Text>
      )}

      {newBackupCodes.length > 0 && (
        <BackupCodesList
          codes={newBackupCodes}
          label="Your new backup codes (your previous codes no longer work):"
        />
      )}

      {/* Password re-auth covers both turning 2FA off and regenerating backup codes. */}
      <Flex column gap="4px">
        <Label htmlFor="password">Password</Label>
        <Input
          name="password"
          control={control}
          type="password"
          placeholder="Password"
          rules={{ required: { value: true, message: "Password is required" } }}
        />
        {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
      </Flex>

      <Flex gap={8}>
        {!companyRequires2FA && (
          <Button
            buttonType="secondary"
            loading={isDisableLoading}
            disabled={!password}
            onClick={onDisable}
          >
            Turn off two-factor authentication
          </Button>
        )}
        <Button
          buttonType="secondary"
          loading={isRegenerateLoading}
          disabled={!password}
          onClick={onRegenerate}
        >
          Regenerate backup codes
        </Button>
      </Flex>
    </Flex>
  );
}
